import { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import styles from './EditorExtension_RecordBrowserPopup.module.css';
import { useCCContext } from '../CC/CCContext';
import { CC } from '../CC/CC';
import * as ccUtils from '../CC/utils';
import { generateQuery, replaceBasketIdSubqueries, postWithCache } from '../CC/utils';
import { getImages } from '../Result/getImages';
import GenericIconButton from '../GenericComponents/GenericIconButton';

const parseStyleString = (styleString) => {
  if (!styleString) return {};

  return styleString.split(';').reduce((styleObj, rule) => {
    const [property, value] = rule.split(':').map(item => item.trim());
    if (property && value) {
      // Convert kebab-case to camelCase
      const camelCaseProperty = property.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
      styleObj[camelCaseProperty] = value;
    }
    return styleObj;
  }, {});
};

// ImageWithFallback.jsx
const ImageWithFallback = ({ src, alt }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setImgSrc(src);
    setLoading(true);
  }, [src]);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setImgSrc(`${process.env.REACT_APP_DZI_BASE_URL}noimage.cci&height=100`);
    setLoading(false);
  };

  return (
    <div className={`${styles.imageWrapper} relative w-full h-full`}>
      {loading && (
        <div className={`${styles.spinner} absolute inset-0 flex justify-center items-center`}>
          <div className={styles.doubleBounce1}></div>
          <div className={styles.doubleBounce2}></div>
        </div>
      )}
      <img
        src={imgSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={`w-full h-full object-cover object-center ${loading ? 'hidden' : 'block'}`}
      />
    </div>
  );
};

const Popup = ({ children, position, onClose }) => {
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Handler to manage click propagation
  const handleClick = (event) => {
    const allowedClasses = [
      'allow-click', // Image
      'allow-click-title', // Title Text
      'allow-click-close', // Close Button
      'allow-click-nav', // Prev/Next Buttons
    ];

    // Check if the clicked element or any of its parents has an allowed class
    let target = event.target;
    let isAllowed = false;
    while (target && target !== popupRef.current) {
      if (allowedClasses.some((cls) => target.classList.contains(cls))) {
        isAllowed = true;
        break;
      }
      target = target.parentElement;
    }

    if (!isAllowed) {
      event.stopPropagation();
      // Optionally, you can prevent default behavior as well
      // event.preventDefault();
    }
  };

  const popupStyle = {
    position: 'absolute',
    top: Math.max(position.y - 200, 10),
    left: position.x,
    zIndex: 1000,
    background: 'white',
    border: '1px solid #ccc',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    maxWidth: '90%',
    maxHeight: '90%',
    overflow: 'auto',
  };

  return ReactDOM.createPortal(
    <div style={popupStyle} ref={popupRef} onClick={handleClick}>
      <div className={styles.closeButton}>
        <GenericIconButton
          icon="light/times.svg"
          width="15px"
          height="15px"
          fill="#000000"
          hoverBackgroundColor="#d8d8d8"
          aria-label="Close"
          className="allow-click-close"
          onClick={(e) => {
            e.stopPropagation(); // Prevent the click from propagating to the Popup's onClick
            onClose();
          }}
        />
      </div>
      {/* Popup Content */}
      {children}
    </div>,
    document.body
  );
};

const RecordBrowserPopup = ({ record, onPrevious, onNext, onClick, isFirst, isLast }) => {
  if (!record) {
    return <div className={styles.noRecord}>No Record Found</div>;
  }

  //console.log(getImages(record));

  const imageUrl =
    getImages(record)["mobilerecord"]?.[0]?.url ||
    `${process.env.REACT_APP_DZI_BASE_URL}noimage.cci&height=640&cache=yes`;

  // New handler for Previous button
  const handlePreviousClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onPrevious();
  };

  // New handler for Next button
  const handleNextClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onNext();
  };

  return (
    <div className={styles.recordBrowserPopup}>
      <div
        className={`${styles.imageContainer} allow-click`}
        onClick={onClick}
      >
        <ImageWithFallback src={imageUrl} alt={record.data.record?.title?.title} />
      </div>
      <div
        className={`${styles.recordDetails} allow-click-title`}
        onClick={onClick}
      >
        <h2 className={styles.title}>{record.data.record?.title?.title}</h2>
        {/* Add more fields here as needed */}
      </div>
      <div className={styles.navigationButtons}>
        <button
          onClick={handlePreviousClick}
          disabled={isFirst}
          className={`${styles.navButton} allow-click-nav`}
        >
          Previous
        </button>
        <button
          onClick={handleNextClick}
          disabled={isLast}
          className={`${styles.navButton} allow-click-nav`}
        >
          Next
        </button>
      </div>
    </div>
  );
};


export default Node.create({
  name: 'reactComponentRecordBrowserPopup',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      selected: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-selected') === 'true',
        renderHTML: (attributes) => ({
          'data-selected': attributes.selected
        }),
      },
      state: {
        default: '',
        parseHTML: (element) => {
          const state = element.getAttribute('data-state');
          return state;
        },
        renderHTML: (attributes) => ({
          'data-state': attributes.state
        }),
      },
      htmlStyle: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-html-style'),
        renderHTML: (attributes) => ({
          'data-html-style': attributes.htmlStyle
        }),
      },
      linkText: {
        default: 'Browse records',
        parseHTML: (element) => element.getAttribute('data-link-text'),
        renderHTML: (attributes) => ({
          'data-link-text': attributes.linkText
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'RecordBrowserPopup',
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['RecordBrowserPopup', mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});

const Component = (props) => {
  const { setCCContext, clearSearch, setViewMode, recordsPerPage } = useCCContext();
  const { selected, updateAttributes } = props;
  const { state, htmlStyle, linkText } = props.node.attrs;
  const [record, setRecord] = useState(null);
  const componentRef = useRef({});
  const containerRef = useRef(null);
  const [currentRecordIndex, setCurrentRecordIndex] = useState(1);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);
  const { user } = useCCContext();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  const fetchRecord = useCallback(async (index) => {
    //alert(state);
    try {
      let stateCC = state ? await ccUtils.GetCCFromState(state.replace(/'/g, '"')) : new CC();
/*      let stateCC = {};
      if (state && state !== '') {
        stateCC = JSON.parse(state);
      } else {
        stateCC = new CC();
      }*/
      let query = generateQuery(stateCC, user);
      query = await replaceBasketIdSubqueries(query);
      const payload = {
        action: "get",
        command: "search",
        query: query,
        range: `${index}-${index}`,
        responseformat: "json"
      };
      const response = await postWithCache(process.env.REACT_APP_CC_URL, payload, componentRef.current);

      if (response.records && response.records.record && response.records.record.data) {
        setRecord(response.records.record);
        setIsFirst(index === 1);
        setIsLast(index >= response.request.count);
      } else {
        setRecord(null);
        setIsLast(true);
      }
    } catch (error) {
      setRecord(null);
      setIsLast(true);
      console.error("Error fetching record:", error);
    }
  }, [state, user]);

  useEffect(() => {
    fetchRecord(currentRecordIndex);
  }, [currentRecordIndex, fetchRecord]);

  useEffect(() => {
    const handleSelection = () => {
      updateAttributes({ selected: props.editor.isActive('reactComponentRecordBrowserPopup') });
    };

    props.editor.on('selectionUpdate', handleSelection);

    return () => {
      props.editor.off('selectionUpdate', handleSelection);
    };
  }, [props.editor, updateAttributes]);

  const handleDoubleClick = (event) => {
    if (props.editor.isEditable) {
      event.stopPropagation();
      const { state, query, htmlStyle, linkText } = props.node.attrs;
      const customEvent = new CustomEvent('showInsertContentPopup', {
        detail: {
          extension: 'RecordBrowserPopup',
          props: { state, query, htmlStyle, linkText },
          pos: props.getPos(),
          node: props.node
        },
        bubbles: true,
        cancelable: true
      });
      event.target.dispatchEvent(customEvent);
    }
  };

  const handlePrevious = () => {
    if (currentRecordIndex > 1) {
      setCurrentRecordIndex(currentRecordIndex - 1);
    }
  };

  const handleNext = () => {
    setCurrentRecordIndex(currentRecordIndex + 1);
  };

  const handleClick = () => {
    if (!props.editor.isEditable) {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('albumexternal')) {
        window.open(`${window.location.origin}${window.location.pathname}?state=${state}&index=${currentRecordIndex}`, '_blank');
      } else {
        if (state) {
          try {
            const defaultCCObj = new CC();
            const startUPCCObj = ccUtils.deepMerge(defaultCCObj, JSON.parse(state));
            setCCContext({
              cc: startUPCCObj,
              first: Math.floor((currentRecordIndex - 1) / recordsPerPage) * recordsPerPage + 1,
              selectedRecord: currentRecordIndex,
              viewMode: 'list',
            });
          } catch (error) {
            console.error("Error parsing state:", error);
          }
        } else {
          clearSearch();
          setViewMode('list');
        }
      }
    }
  };

  const handlePopupOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setPopupPosition({ x: rect.left, y: rect.bottom + window.scrollY });
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
  };

  return (
    <NodeViewWrapper as="span" className={`react-component ${selected ? 'selected' : ''}`}>
      <span
        ref={containerRef}
        onDoubleClick={handleDoubleClick}
        onClick={handlePopupOpen}
        style={{ cursor: 'pointer' }}
      >
        <span>
          {'' + linkText === 'undefined' ? 'Browse records' : linkText}
          {isPopupOpen && (
            <Popup position={popupPosition} onClose={handlePopupClose}>
              <span className={`${styles.recordBrowserPopupContainer}`} style={parseStyleString(htmlStyle)}>
                <RecordBrowserPopup
                  record={record}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  isFirst={isFirst}
                  isLast={isLast}
                  onClick={handleClick}
                />
              </span>
            </Popup>
          )}
        </span>
      </span>
    </NodeViewWrapper>
  );
};
